import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Collapse} from 'react-collapse';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {Alert, Button, DatePicker, Divider, Form, Input, Select, Space} from 'antd';
import dayjs from 'dayjs';
import {InfoCircleOutlined} from '@ant-design/icons';
import {StyledAddExpenseModal, StyledAddExpenseModalTitle} from './StyledAddExpenseModal';
import EuroInput from '../../../EuroInput';
import MissingFieldIndicator from '../../../MissingFieldIndicator';
import SpinSmall from '../../../SpinSmall';
import SuggestionPopover from '../SuggestionPopover';
import InvoiceDetailsPanel from '../../TransactionsPage/InvoiceDetailsPanel';
import {expensesHelpers, formHelpers, objectHelpers} from '../../../../utils/helpers';
import {expenseTypesConstants} from '../../../../constants';
import {useFormHelpers} from '../../../../hooks';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {expenseActions} from '../../../../state/actions/expense.actions';

const {Item} = Form;

const initialFormValues = {
  category_tags: undefined,
  currency: 'EUR',
  description: '',
  date: [dayjs(), dayjs()],
  expense_type: undefined,
  invoice_id: undefined,
  price: '',
};

const defaultAdditionalFormFields = {
  expense_type: undefined,
  invoice_id: undefined,
}

const defaultError = {
  message: null,
  open: false
};

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0 || value.every(isEmptyValue);
  return false;
};

const getMissingLabel = (label, value) => (
  <Space size='small' align='center'>
    {label}
    {isEmptyValue(value) && <MissingFieldIndicator />}
  </Space>
);

const AddExpenseModal = ({
  onSuccess,
  skipClosingAfterSuccess = false,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'expenses']);
  const [form] = Form.useForm();
  const descriptionValue = Form.useWatch('description', form);
  const categoryValue = Form.useWatch('category_tags', form);
  const amountValue = Form.useWatch('price', form);
  const dateValue = Form.useWatch('date', form);
  const [error, setError] = useState(defaultError);
  const [loading, setLoading] = useState(false);
  const [additionalFormFields, setAdditionalFormFields] = useState(defaultAdditionalFormFields);
  const isDefinedExpenseType = objectHelpers.isValidValue(additionalFormFields?.expense_type);

  const { expenseTags, open } = useSelector(({ company, expense }) => ({
    expenseTags: company?.expenseTags || [],
    open: expense.addExpenseModalProps.open,
  }));

  const dispatch = useDispatch();

  const {placeholderEnter, placeholderSelect} = useFormHelpers();

  const expenseCategoryOptions = useMemo(() => formHelpers.getTagOptions(expenseTags), [expenseTags]);

  const handleSelectSuggestion = (suggestion) => {
    form.setFieldsValue({description: suggestion});
  }

  const clearError = () => {
    if (error.open) {
      setError({...error, open: false});
    }
  }

  const handleAfterOpenChange = (open) => {
    if (!open) {
      form.setFieldsValue(initialFormValues);
      clearError();
      setAdditionalFormFields(defaultAdditionalFormFields);
      if (loading) setLoading(false);
    }
  }

  const handleSubmit = (fields, {isDraft = false} = {}) => {
    // additional validation: ensure expense_type is defined (set from InvoiceDetailsPanel or other flow)
    // If expense_type indicates an invoice, ensure invoice_id is present
    if (!isDraft && additionalFormFields?.expense_type === undefined) {
      setError({
        message: t('expenses:validation.invoiceValuesNotDefined'),
        open: true
      });
      return;
    }

    setLoading(true);

    const data = {
      ...fields,
      ...additionalFormFields,
      currency: 'EUR',
      category_tags: fields.category_tags ? [fields.category_tags] : [],
      expense_date: formHelpers.getFormattedDateValue(fields.date?.[0] || dayjs()),
      expense_end_date: formHelpers.getFormattedDateValue(fields.date?.[1]),
    };
    delete data.date;

    dispatch(expenseActions.createExpense({
      data,
      successCallback: (response) => {
        expensesHelpers.addExpenseSuggestion(response?.description || data.description);
        if (skipClosingAfterSuccess) return;

        setLoading(false);
        onSuccess?.(response);
        dispatch(expenseActions.closeAddExpenseModal());
      },
      errorCallback: (err) => {
        if (err.message) {
          setError({ ...error, message: err.message, open: true });
        } else {
          const fields = formHelpers.getFormServerErrorFields(err);
          form.setFields(fields);
        }
        setLoading(false);
      }
    }));
  }

  const handleRemoveInvoice = ({successCallback}) => {
    const invoiceId = additionalFormFields.invoice_id;
    if (invoiceId) {
      dispatch(invoicesActions.batchDeleteInvoices([invoiceId]));
    }
    setAdditionalFormFields(defaultAdditionalFormFields);
    successCallback?.();
  }

  const handleExpenseUpdate = (updatedExpense) => {
    setAdditionalFormFields({...additionalFormFields, ...updatedExpense});
    clearError();
  }

  const handleAddInvoice = (newInvoice) => {
    setAdditionalFormFields({
      ...additionalFormFields,
      invoice_id: newInvoice.id,
      expense_type: expenseTypesConstants.INVOICE
    });
    clearError();
  };

  const tooltip = (key) => t(`expenses:modal.addExpense.tooltip.${key}`);

  const onClose = () => dispatch(expenseActions.closeAddExpenseModal());

  const rules = [{required: true, message: t('validation.fieldIsRequired')}];

  const isFormReady = Boolean(
    isDefinedExpenseType
    && !isEmptyValue(descriptionValue)
    && !isEmptyValue(categoryValue)
    && !isEmptyValue(amountValue)
    && !isEmptyValue(dateValue?.[0])
  );

  const isDraftReady = !isEmptyValue(amountValue);

  const handleSaveDraft = async () => {
    try {
      await form.validateFields(['price']);
      const fields = form.getFieldsValue(true);
      handleSubmit(fields, {isDraft: true});
    } catch (_) {
      // amount validation failed
    }
  };

  const footer = [
    <Button
      key='save-as-draft'
      disabled={loading || !isDraftReady}
      onClick={handleSaveDraft}
    >
      {t('saveAsDraft')}
    </Button>,
    <Button
      key='file-expense'
      disabled={loading || !isFormReady}
      onClick={() => form.submit()}
      type='primary'
    >
      {t('File')} {t('expense')}
    </Button>
  ];

  return (
    <StyledAddExpenseModal
      afterOpenChange={handleAfterOpenChange}
      footer={footer}
      forceRender
      onClose={onClose}
      open={open}
      title={`${t('new')} ${t('expense')}`}
      width={480}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <InvoiceDetailsPanel
          key={String(open)}
          allowActionsWithoutExpense={true}
          displayMissingFieldIndicator={!isDefinedExpenseType}
          download={false}
          onExpenseUpdate={handleExpenseUpdate}
          onInvoiceAdd={handleAddInvoice}
          onInvoiceRemove={handleRemoveInvoice}
        />
        <Divider />
        <Form
          initialValues={initialFormValues}
          form={form}
          layout='vertical'
          onFinish={(fields) => handleSubmit(fields, {isDraft: false})}
          requiredMark={false}
        >
          <StyledAddExpenseModalTitle>
            {t('expense')} {t('details')}
          </StyledAddExpenseModalTitle>
          <Item
            label={getMissingLabel(t('description'), descriptionValue)}
            name='description'
            required
            rules={rules}
          >
            <SuggestionPopover onSelect={handleSelectSuggestion} key={String(open)}>
              <Input.TextArea placeholder={placeholderEnter('description')} size='large' />
            </SuggestionPopover>
          </Item>
          <Item
            label={getMissingLabel(t('amount'), amountValue)}
            name='price'
            required
            rules={rules}
          >
            <EuroInput placeholder={placeholderEnter('amount')} />
          </Item>
          <Item
            label={getMissingLabel(t('category'), categoryValue)}
            name='category_tags'
            required
            rules={rules}
          >
            <Select
              options={expenseCategoryOptions}
              placeholder={placeholderSelect('expense type')}
              size='large'
              showSearch
            />
          </Item>
          <Item
            label={t('date')}
            name='date'
            tooltip={{icon: <InfoCircleOutlined />, title: tooltip('date'), placement: 'right'}}
          >
            <DatePicker.RangePicker
              size='large'
              format='DD MMM YYYY'
              placeholder={[t('from'), t('to')]}
              allowEmpty={[false, true]}
            />
          </Item>
        </Form>
        <Collapse isOpened={error.open}>
          <Alert message={error.message} type='error' showIcon />
        </Collapse>
      </SpinSmall>
    </StyledAddExpenseModal>
  );
}

AddExpenseModal.propTypes = {
  onSuccess: PropTypes.func,
  skipClosingAfterSuccess: PropTypes.bool,
}

export default AddExpenseModal;
